import React from 'react';
import { Helmet } from 'react-helmet';
import { FaCheckCircle, FaAward, FaUsers, FaHospital } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Base from './Base';

function About() {
  return (
    <Base>
      <div className="dark:bg-dark-800 min-h-screen transition-colors duration-200">
        <Helmet><title>About Us | Aman Medical Complex Gilgit</title></Helmet>

        {/* Page Title Section */}
        <section className="relative py-12 md:py-16 overflow-hidden bg-gradient-to-br from-primary-50 to-white dark:from-dark-800 dark:to-dark-900">
          <div className="absolute inset-0 z-0 opacity-5">
            <div className="absolute top-10 left-10 w-72 h-72 bg-primary-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-primary-300 rounded-full blur-3xl"></div>
          </div>
          <div className="container mx-auto px-4 text-center relative z-10">
            <span className="text-primary-500 font-bold uppercase tracking-[0.2em] text-xs md:text-sm inline-block mb-4">ABOUT US</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-navy-500 dark:text-white mb-6 tracking-tight">About Us</h1>
            <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 font-semibold text-sm">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <span>/</span>
              <span className="text-primary-500">About Us</span>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-12 container mx-auto px-4">
          {/* Mission and Vision Introduction Section */}
          <div className="mb-12">
            <div className="relative overflow-hidden bg-gradient-to-br from-navy-500 via-navy-600 to-navy-700 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 rounded-[40px] p-8 md:p-10 lg:p-12 shadow-2xl border border-navy-400/20 dark:border-dark-700">
              {/* Decorative Background Elements */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-400 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary-300 rounded-full blur-3xl"></div>
              </div>
              
              {/* Content */}
              <div className="relative z-10 max-w-5xl mx-auto text-center">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-6 py-2 rounded-full mb-4">
                  <span className="w-2 h-2 bg-primary-400 rounded-full animate-pulse"></span>
                  <span className="text-white/90 font-bold uppercase tracking-[0.2em] text-xs">FOUNDATION</span>
                  <span className="w-2 h-2 bg-primary-400 rounded-full animate-pulse"></span>
                </div>

                {/* Title */}
                <h2 className="text-xl md:text-2xl lg:text-3xl font-black text-white mb-4 leading-tight tracking-tight">
                  Mission and Vision of<br />
                  <span className="text-primary-300">Aman Medical Complex</span>
                </h2>

                {/* Main Content */}
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 md:p-8 mb-4">
                  <p className="text-white/95 text-sm md:text-base leading-relaxed font-medium">
                    Aman Medical Complex (AMC) Gilgit is established in loving memory of our father, as a service-driven initiative dedicated to the people of Gilgit-Baltistan. Our foremost purpose is not to earn profit or perform it as business, but to provide high-quality healthcare with compassion and integrity.
                  </p>
                </div>

                {/* Decorative Quote Marks */}
                <div className="flex items-center justify-center gap-4 text-white/20">
                  <div className="w-16 h-px bg-white/30"></div>
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 9.005-9.57v3.343c-1.763 0-3.415.781-4.746 2.172 1.742 1.688 2.733 4.006 2.733 6.58v7.886h-7.017zm-14.017 0v-7.391c0-5.704 3.748-9.57 9.005-9.57v3.343c-1.763 0-3.415.781-4.746 2.172 1.742 1.688 2.733 4.006 2.733 6.58v7.886h-7.017z"/>
                  </svg>
                  <div className="w-16 h-px bg-white/30"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Chairman Message Section */}
          <div className="mb-12">
            <div className="relative overflow-hidden bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 dark:from-dark-800 dark:via-dark-900 dark:to-dark-800 rounded-[40px] p-6 md:p-8 lg:p-10 shadow-2xl border border-primary-400/30 dark:border-dark-700">
              {/* Decorative Background Elements */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-80 h-80 bg-navy-300 rounded-full blur-3xl"></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center relative z-10">
                {/* Chairman Image */}
                <div className="lg:col-span-1 flex justify-center lg:justify-start">
                  <div className="relative group">
                    <div className="absolute inset-0 bg-primary-400 rounded-[40px] blur-xl opacity-50 group-hover:opacity-70 transition-opacity"></div>
                    <img
                      src="/ceo/chairmen.jpeg"
                      alt="Chairman - Aman Medical Complex"
                      className="relative w-56 h-72 md:w-64 md:h-80 rounded-[40px] object-cover shadow-2xl border-4 border-white/20 dark:border-dark-700 transform group-hover:scale-105 transition-transform duration-500"
                      style={{ objectPosition: 'center center' }}
                    />
                    <div className="absolute -bottom-4 -right-4 bg-white dark:bg-dark-800 text-primary-600 dark:text-primary-400 px-5 py-3 rounded-2xl shadow-2xl border-2 border-primary-400/30">
                      <p className="text-xs font-black uppercase tracking-wider">CHAIRMAN</p>
                    </div>
                    {/* Decorative Corner Badge */}
                    <div className="absolute -top-2 -left-2 w-16 h-16 bg-primary-400/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30">
                      <div className="w-8 h-8 bg-primary-400 rounded-full"></div>
                    </div>
                  </div>
                </div>

                {/* Chairman Message */}
                <div className="lg:col-span-2">
                  <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full mb-4">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                    <span className="text-white/90 font-bold uppercase tracking-[0.2em] text-xs">CHAIRMAN MESSAGE</span>
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  </div>
                  
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-black text-white mb-4 leading-tight tracking-tight">
                    Message from Our Chairman
                  </h2>
                  
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-5 md:p-6 mb-4">
                    <p className="text-white/95 mb-3 leading-relaxed text-sm md:text-base font-medium italic">
                      "Aman Medical Complex Gilgit stands as a testament to our commitment to serving the people of Gilgit-Baltistan. Established in loving memory of our father, this institution represents our family's dedication to providing exceptional healthcare services to our community."
                    </p>
                    <p className="text-white/95 leading-relaxed text-sm md:text-base font-medium italic">
                      "Our vision extends beyond mere medical treatment—we strive to create a healthcare ecosystem that is accessible, compassionate, and built on the foundation of trust and integrity. Every decision we make, every service we provide, is guided by our core principle: healthcare is a right, not a privilege."
                    </p>
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/20">
                    <p className="text-lg font-black text-white mb-1">Chairman</p>
                    <p className="text-primary-200 dark:text-primary-300 font-semibold text-sm">Aman Medical Complex Gilgit</p>
                    <p className="text-white/70 text-xs mt-1">Dedicated to Excellence in Healthcare</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CEO Message Section - Top */}
          <div className="mb-12">
            <div className="bg-gradient-to-br from-primary-50 to-white dark:from-dark-800 dark:to-dark-900 rounded-3xl p-6 md:p-8 lg:p-10 shadow-strong border border-gray-100 dark:border-dark-700">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                {/* CEO Image */}
                <div className="lg:col-span-1 flex justify-center lg:justify-start">
                  <div className="relative">
                    <img
                      src="/ceo/ceoeh.jpeg"
                      alt="Dr Ehsan Ullah - CEO"
                      className="w-56 h-72 md:w-64 md:h-80 rounded-3xl object-cover shadow-xl border-4 border-white dark:border-dark-700"
                      style={{ objectPosition: 'center 20%' }}
                    />
                    <div className="absolute -bottom-4 -right-4 bg-primary-500 text-white px-4 py-2 rounded-xl shadow-lg">
                      <p className="text-xs font-bold uppercase">CEO</p>
                    </div>
                  </div>
                </div>

                {/* CEO Message */}
                <div className="lg:col-span-2">
                  <span className="text-primary-500 font-bold uppercase tracking-[0.2em] text-xs mb-3 inline-block">CEO MESSAGE</span>
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-black text-navy-500 dark:text-white mb-3 leading-tight">Message from Dr. Ehsan Ullah</h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed text-sm md:text-base italic">
                    "At Aman Medical Complex Gilgit, we are committed to providing exceptional healthcare services that combine medical excellence with compassionate care. Our vision is to be the most trusted medical institution in the region, and our mission is to ensure that quality healthcare is accessible to everyone, regardless of their background or circumstances."
                  </p>
                  <p className="text-gray-600 dark:text-gray-400 mb-3 leading-relaxed text-sm md:text-base italic">
                    "We believe that healthcare is not just about treating illnesses, but about building lasting relationships with our patients and the community. Every day, our dedicated team works tirelessly to uphold the highest standards of medical care while maintaining the human touch that makes all the difference."
                  </p>
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-700">
                    <p className="text-base font-black text-navy-500 dark:text-white">Dr. Ehsan Ullah</p>
                    <p className="text-primary-500 font-semibold text-sm">Chief Executive Officer</p>
                    <p className="text-gray-600 dark:text-gray-400 text-xs">Aman Medical Complex Gilgit</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mission & Vision Section - Bottom */}
          <div className="relative">
            {/* Background Decorative Elements */}
            <div className="absolute inset-0 -z-10 opacity-5">
              <div className="absolute top-1/4 left-0 w-72 h-72 bg-primary-400 rounded-full blur-3xl"></div>
              <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-navy-300 rounded-full blur-3xl"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
              {/* Left Side - Mission & Vision */}
              <div className="space-y-6">
                {/* Mission Section */}
                <div className="group relative bg-white dark:bg-dark-800 rounded-[32px] p-6 md:p-8 shadow-card hover:shadow-card-hover border border-gray-100 dark:border-dark-700 transition-all duration-500 card-hover-lift">
                  {/* Decorative Corner Element */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-primary-50 dark:bg-primary-900/10 rounded-bl-[32px] rounded-tr-[32px] opacity-50 group-hover:opacity-100 transition-opacity"></div>
                  
                  {/* Icon Badge */}
                  <div className="inline-flex items-center gap-2 bg-primary-50 dark:bg-primary-900/20 px-4 py-2 rounded-full mb-4 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors">
                    <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></span>
                    <span className="text-primary-500 font-black uppercase tracking-[0.2em] text-xs">OUR MISSION</span>
                    <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></span>
                  </div>
                  
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-black text-navy-500 dark:text-white mb-4 leading-tight group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors">
                    Our Mission
                  </h2>
                  
                  <div className="relative z-10">
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm md:text-base font-medium">
                      To deliver accessible, reliable, and compassionate medical care to individuals from all walks of life. Rooted in sincerity and the spirit of public service, we aim to create this hospital that stands as a lasting symbol of care, trust, and service to community.
                    </p>
                  </div>

                  {/* Decorative Line */}
                  <div className="mt-6 pt-4 border-t border-gray-100 dark:border-dark-700">
                    <div className="w-16 h-1 bg-gradient-to-r from-primary-500 to-primary-300 rounded-full"></div>
                  </div>
                </div>

                {/* Vision Section */}
                <div className="group relative bg-white dark:bg-dark-800 rounded-[32px] p-6 md:p-8 shadow-card hover:shadow-card-hover border border-gray-100 dark:border-dark-700 transition-all duration-500 card-hover-lift">
                  {/* Decorative Corner Element */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-primary-50 dark:bg-primary-900/10 rounded-bl-[32px] rounded-tr-[32px] opacity-50 group-hover:opacity-100 transition-opacity"></div>
                  
                  {/* Icon Badge */}
                  <div className="inline-flex items-center gap-2 bg-primary-50 dark:bg-primary-900/20 px-4 py-2 rounded-full mb-4 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors">
                    <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></span>
                    <span className="text-primary-500 font-black uppercase tracking-[0.2em] text-xs">OUR VISION</span>
                    <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></span>
                  </div>
                  
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-black text-navy-500 dark:text-white mb-4 leading-tight group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors">
                    Our Vision
                  </h2>
                  
                  <div className="relative z-10">
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm md:text-base font-medium">
                      We aspire to develop Medical Hospital Gilgit into one of the most trusted and respected medical institution in the region—known for its excellence in healthcare, modern facilities, and commitment to human wellbeing.
                    </p>
                  </div>

                  {/* Decorative Line */}
                  <div className="mt-6 pt-4 border-t border-gray-100 dark:border-dark-700">
                    <div className="w-16 h-1 bg-gradient-to-r from-primary-500 to-primary-300 rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Right Side - Image */}
              <div className="relative lg:sticky lg:top-24">
                <div className="relative group">
                  {/* Main Image Container */}
                  <div className="relative overflow-hidden rounded-[40px] shadow-2xl border-4 border-white dark:border-dark-700">
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent z-10"></div>
                    
                    <img
                      src="/assets/images/mv.png"
                      alt="Hospital team - Doctor and Patient"
                      className="w-full h-[450px] md:h-[500px] object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>

                  {/* Experience Badge - Premium Design */}
                  <div className="absolute -bottom-4 -left-4 bg-white dark:bg-dark-800 p-6 md:p-8 rounded-[32px] shadow-2xl border-4 border-primary-200 dark:border-primary-900/30 transform group-hover:scale-105 transition-transform duration-300 hidden md:block">
                    {/* Decorative Background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-white dark:from-primary-900/10 dark:to-dark-800 rounded-[32px] opacity-50"></div>
                    
                    {/* Content */}
                    <div className="relative z-10">
                      <div className="flex items-baseline gap-2 mb-1">
                        <p className="text-4xl md:text-5xl font-black text-primary-500 dark:text-primary-400 leading-none">15+</p>
                        <span className="text-lg font-black text-primary-400 dark:text-primary-500">+</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-1 bg-gradient-to-r from-primary-500 to-primary-300 rounded-full"></div>
                        <p className="text-xs font-black text-navy-500 dark:text-white uppercase tracking-[0.2em]">Years Experience</p>
                      </div>
                    </div>

                    {/* Decorative Corner */}
                    <div className="absolute -top-2 -right-2 w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>

                  {/* Floating Decorative Elements */}
                  <div className="absolute top-8 right-8 w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 md:py-16 bg-gradient-to-br from-navy-500 to-navy-700 dark:from-dark-900 dark:to-dark-800">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { icon: <FaUsers />, number: "25k+", label: "Happy Patients" },
                { icon: <FaHospital />, number: "150+", label: "Expert Doctors" },
                { icon: <FaAward />, number: "310+", label: "Awards Won" },
                { icon: <FaCheckCircle />, number: "24/7", label: "Support Available" }
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center text-primary-400 text-3xl mx-auto mb-4">
                    {stat.icon}
                  </div>
                  <h3 className="text-3xl font-black text-white mb-2">{stat.number}</h3>
                  <p className="text-white/80 font-semibold text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Base>
  );
}

export default About;
